/*
 * Session-only UI permissions, set by the parent window of the IFrame Remote.
 * Nothing in this store is persisted to localStorage, so a role imposed by an
 * integrator never leaks into the user's own UI settings.
 */

export const METADATA_KINDS = ["notes", "approvals", "forced", "speakers", "recipients", "tags"];

function defaultMetadata() {
    return Object.fromEntries(METADATA_KINDS.map((kind) => [kind, true]));
}

function defaultPermissions() {
    return {
        role: "editor", // guest | reviewer | editor | custom
        primaryToolbar: null, // null = default; else { menus: { id: true | false | [itemIds] }, buttons: { id: bool } }
        quickToolsPanel: true, // false hides the whole QuickTools panel
        quickToolsAllowed: null, // null = no overlay; else array of allowed panel ids
        timeline: true, // false hides the timeline and its toggle button
        editingLocked: false, // role-wide text/timing lock for every event group
        metadata: defaultMetadata(),
    };
}

export const permissionState = $state({
    ...defaultPermissions(),
    lockedGroupIds: [], // event group ids locked by the parent window
});

/**
 * Resets every role/UI permission back to full access. Event groups locked by
 * the parent with lockEventGroup() stay locked; those are cleared separately.
 */
export function resetPermissions() {
    Object.assign(permissionState, defaultPermissions());
}

/* Metadata */

/**
 * Whether the given kind of metadata is available in the UI.
 * @param {"notes"|"approvals"|"forced"|"speakers"|"recipients"|"tags"} kind
 */
export function canEditMetadata(kind) {
    return permissionState.metadata[kind] !== false;
}

const METADATA_TABLE_COLUMNS = {
    notes: "notes",
    reply: "notes",
    notesCheck: "notes",
    replyCheck: "notes",
    approved: "approvals",
    forced: "forced",
    speakers: "speakers",
    recipients: "recipients",
    tags: "tags",
};

/**
 * The metadata kind an events-table column edits, or undefined for content columns.
 * Metadata columns stay editable on locked event groups.
 * @param {string} columnId
 */
export function getMetadataKindForTableColumn(columnId) {
    return METADATA_TABLE_COLUMNS[columnId];
}

export function isTableColumnAllowed(columnId) {
    const kind = METADATA_TABLE_COLUMNS[columnId];
    return !kind || canEditMetadata(kind);
}

export function setMetadataPermissions(settings) {
    if (settings === null || settings === undefined) {
        permissionState.metadata = defaultMetadata();
        return { success: true, metadata: { ...permissionState.metadata }, ignored: [] };
    }

    if (typeof settings !== "object" || Array.isArray(settings)) {
        throw new Error("Metadata permissions must be an object, null, or undefined.");
    }

    const ignored = [];
    Object.entries(settings).forEach(([kind, value]) => {
        if (!METADATA_KINDS.includes(kind) || typeof value !== "boolean") {
            ignored.push(kind);
            return;
        }
        permissionState.metadata[kind] = value;
    });

    permissionState.role = "custom";
    return { success: true, metadata: { ...permissionState.metadata }, ignored };
}

/* Event group locks */

export function isEditingLockedByPermissions() {
    return permissionState.editingLocked;
}

/**
 * Whether the group is locked by the parent window or by the active role.
 * These locks cannot be removed from the Event Group Options modal.
 * @param {{id?: string} | undefined} group
 */
export function isGroupLockedExternally(group) {
    if (!group) {
        return false;
    }
    return permissionState.editingLocked || permissionState.lockedGroupIds.includes(group.id);
}

export function setGroupParentLock(groupId, locked) {
    const index = permissionState.lockedGroupIds.indexOf(groupId);
    if (locked && index === -1) {
        permissionState.lockedGroupIds.push(groupId);
    } else if (!locked && index !== -1) {
        permissionState.lockedGroupIds.splice(index, 1);
    }
}

/* QuickTools */

const METADATA_QUICK_TOOLS_PANELS = {
    notes: ["notes"],
    speakers: ["speakers", "recipients"],
    tags: ["tags"],
};

/**
 * Permission overlay on top of the user's own QuickTools settings.
 * @param {string} panelId
 */
export function isQuickToolsPanelAllowed(panelId) {
    if (!permissionState.quickToolsPanel) {
        return false;
    }

    const metadataKinds = METADATA_QUICK_TOOLS_PANELS[panelId];
    if (metadataKinds && !metadataKinds.some(canEditMetadata)) {
        return false;
    }

    return !permissionState.quickToolsAllowed || permissionState.quickToolsAllowed.includes(panelId);
}

/* Primary toolbar */

export function isMenuAllowed(menuId) {
    const menus = permissionState.primaryToolbar?.menus;
    return !menus || menus[menuId] !== false;
}

export function isMenuItemAllowed(menuId, itemId) {
    const menus = permissionState.primaryToolbar?.menus;
    if (!menus || !menuId) {
        return true;
    }

    const setting = menus[menuId];
    if (setting === false) {
        return false;
    }
    if (Array.isArray(setting)) {
        return Boolean(itemId) && setting.includes(itemId);
    }
    return true;
}

export function isToolbarButtonAllowed(buttonId) {
    if (buttonId === "timeline" && !permissionState.timeline) {
        return false;
    }
    const buttons = permissionState.primaryToolbar?.buttons;
    return !buttons || buttons[buttonId] !== false;
}

/* Timeline */

export function isTimelineEnabled() {
    return permissionState.timeline;
}
